/*
 * W1 Science — the behaviour behind the w1-science-* sections.
 *
 * Four small pieces, each wired to a data attribute the section writes:
 *   [data-w1sc-subnav]    the sticky in-page nav: a shadow once it is stuck,
 *                         and the underline following the section in view
 *   [data-w1sc-tabs]      the scenario tabs under "Where W1 fits"
 *   [data-w1sc-research]  each area's "View more" and the fold on each
 *                         study row
 *   .w1sc-reveal          fade-in on scroll, only ever hidden under html.w1sc-js
 *
 * Everything is already in the markup. The script moves hidden attributes,
 * aria state and class names; nothing is fetched or rendered here, so a page
 * with the script blocked still reads in full.
 *
 * Runs once per element. The theme editor re-renders a single section without
 * reloading the page, so the same wiring has to survive being asked twice.
 */
(function () {
  'use strict';

  var raf =
    window.requestAnimationFrame ||
    function (fn) {
      return window.setTimeout(fn, 16);
    };

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  /* ---------------------------------------------------------------------
     Sticky sub-nav
     --------------------------------------------------------------------- */
  function wireSubnav(nav) {
    if (nav.dataset.w1scReady === 'true') return;
    nav.dataset.w1scReady = 'true';

    var links = toArray(nav.querySelectorAll('[data-w1sc-target]'));
    if (!links.length) return;

    // The element that actually sticks is Shopify's section wrapper, one or
    // two levels up; its computed top is where "stuck" begins.
    var sticky = nav.closest('.w1sc-section--nav') || nav;

    function sections() {
      return links
        .map(function (link) {
          return document.getElementById(link.dataset.w1scTarget);
        })
        .filter(Boolean);
    }

    function setActive(id) {
      links.forEach(function (link) {
        link.classList.toggle('is-active', link.dataset.w1scTarget === id);
      });
    }

    function pick() {
      var rect = sticky.getBoundingClientRect();
      var stickyTop = parseFloat(window.getComputedStyle(sticky).top) || 0;
      nav.classList.toggle('is-stuck', rect.top <= stickyTop + 1);

      // The reading line sits a little under the sticky stack, so a section
      // counts as current once its heading has passed under the bar.
      var line = rect.bottom + 120;
      var current = null;
      var list = sections();
      list.forEach(function (section) {
        if (section.getBoundingClientRect().top <= line) current = section.id;
      });

      // Bottom of the page: the last target may be too short to ever cross
      // the line on its own.
      var doc = document.documentElement;
      if (list.length && window.innerHeight + window.pageYOffset >= doc.scrollHeight - 4) {
        current = list[list.length - 1].id;
      }
      setActive(current);
    }

    var pending = 0;
    function onScroll() {
      if (pending) return;
      pending = raf(function () {
        pending = 0;
        pick();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    pick();
  }

  /* ---------------------------------------------------------------------
     Scenario tabs
     --------------------------------------------------------------------- */
  function selectTab(tabs, tab) {
    tabs.forEach(function (candidate) {
      var on = candidate === tab;
      candidate.setAttribute('aria-selected', on ? 'true' : 'false');
      candidate.tabIndex = on ? 0 : -1;

      // getElementById rather than a selector, because a section id in the
      // theme editor looks like `template--17...__hero` and would have to be
      // escaped before it could go in a query.
      var panel = document.getElementById(candidate.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  }

  function wireTabs(root) {
    if (root.dataset.w1scReady === 'true') return;
    root.dataset.w1scReady = 'true';

    var tabs = toArray(root.querySelectorAll('[data-w1sc-tab]'));
    if (tabs.length < 2) return;

    root.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-w1sc-tab]');
      if (tab && tabs.indexOf(tab) !== -1) selectTab(tabs, tab);
    });

    root.addEventListener('keydown', function (event) {
      var index = tabs.indexOf(document.activeElement);
      if (index === -1) return;

      var next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = tabs[(index + 1) % tabs.length];
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = tabs[(index - 1 + tabs.length) % tabs.length];
      } else if (event.key === 'Home') {
        next = tabs[0];
      } else if (event.key === 'End') {
        next = tabs[tabs.length - 1];
      }
      if (!next) return;

      event.preventDefault();
      next.focus();
      selectTab(tabs, next);
    });
  }

  /* ---------------------------------------------------------------------
     Research: each area's View more, and the fold on each study row
     --------------------------------------------------------------------- */
  function setOpen(study, open) {
    study.classList.toggle('is-open', open);
    var button = study.querySelector('[data-w1sc-toggle]');
    if (button) button.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // A group shows its first few rows; the rest are kept out of sight by the
  // stylesheet until the group is expanded. The button's label is swapped
  // between the two the section printed on it, and the topics chip beside
  // the label is hidden by the stylesheet while the group is open.
  function setExpanded(group, expanded) {
    group.classList.toggle('is-expanded', expanded);
    var button = group.querySelector('[data-w1sc-group-more]');
    if (!button) return;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var label = button.querySelector('[data-w1sc-group-more-label]');
    if (label) {
      label.textContent = expanded ? button.dataset.lessLabel || label.textContent : button.dataset.moreLabel || label.textContent;
    }
  }

  function wireResearch(root) {
    if (root.dataset.w1scReady === 'true') return;
    root.dataset.w1scReady = 'true';

    root.addEventListener('click', function (event) {
      var more = event.target.closest('[data-w1sc-group-more]');
      if (more) {
        var group = more.closest('[data-w1sc-group]');
        if (group) setExpanded(group, more.getAttribute('aria-expanded') !== 'true');
        return;
      }

      var toggle = event.target.closest('[data-w1sc-toggle]');
      if (toggle && !toggle.disabled) {
        var study = toggle.closest('[data-w1sc-study]');
        if (study) setOpen(study, toggle.getAttribute('aria-expanded') !== 'true');
      }
    });
  }

  /* ---------------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------------- */
  var revealObserver = null;

  function wireReveal(scope) {
    var targets = toArray(scope.querySelectorAll('.w1sc-reveal:not(.is-visible)'));
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    targets.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot(scope) {
    var root = scope || document;
    toArray(root.querySelectorAll('[data-w1sc-subnav]')).forEach(wireSubnav);
    toArray(root.querySelectorAll('[data-w1sc-tabs]')).forEach(wireTabs);
    toArray(root.querySelectorAll('[data-w1sc-research]')).forEach(wireResearch);
    wireReveal(root);
  }

  boot();
  document.addEventListener('DOMContentLoaded', function () {
    boot();
  });
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  // Picking a scenario block in the theme editor brings its tab forward, so
  // the merchant sees the panel they are editing rather than the first one;
  // picking a study unfolds its row, and the area it waits behind if it is
  // one of those past the area's View more.
  document.addEventListener('shopify:block:select', function (event) {
    var tab = event.target.closest('[data-w1sc-tab]');
    if (tab) {
      var root = tab.closest('[data-w1sc-tabs]');
      if (root) selectTab(toArray(root.querySelectorAll('[data-w1sc-tab]')), tab);
      return;
    }

    var study = event.target.closest('[data-w1sc-study]');
    if (study) {
      var group = study.closest('[data-w1sc-group]');
      if (group && study.hasAttribute('data-w1sc-extra') && !group.classList.contains('is-expanded')) {
        setExpanded(group, true);
      }
      var toggle = study.querySelector('[data-w1sc-toggle]');
      if (toggle && !toggle.disabled) setOpen(study, true);
    }
  });
})();
